<template>
  <div class="base-select" :class="modifierClassnames">
    <template v-if="label">
      <label :for="id" class="base-select__label">
        {{ label }}
      </label>
    </template>
    <div class="base-select__select-wrapper">
      <span class="caret"></span>
      <select :id="id" v-model="model" :name="name" :data-testid="testId">
        <option v-if="!hideEmptyOption" :value="undefined" selected class="base-select__option --placeholder">
          {{ emptyOptionLabel }}
        </option>
        <option v-for="option in options" :key="option.value" :value="option.value" class="base-select__option">
          {{ option.label }}
        </option>
      </select>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: {
    type: String,
    default: undefined,
  },
  id: {
    type: String,
    default: undefined,
  },
  name: {
    type: String,
    default: undefined,
  },
  testId: {
    type: String,
    default: undefined,
  },
  label: {
    type: String,
    default: undefined,
  },
  options: {
    type: Array,
    required: true,
  },
  hideEmptyOption: {
    type: Boolean,
    default: false,
  },
  emptyOptionLabel: {
    type: String,
    default: "Choose one",
  },
});

const model = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const modifierClassnames = computed(() => ({
  "--has-value": model.value,
}));
</script>

<style lang="scss" scoped>
.base-select {
  --caret-size: var(--sp-comp-dtv-select-caret-size, 0.75rem);
  --color: var(--sp-comp-dtv-select-color, var(--sp-sys-color-on-surface, #333));
  --placeholder-color: var(--sp-comp-dtv-select-placeholder-color, var(--sp-sys-form-field-placeholder-color, #ddd));
  --padding-inline: var(--sp-comp-dtv-select-padding-inline, var(--sp-sys-form-field-padding-inline));
  --caret-right-offset: var(--padding-inline);
}

.base-select__select-wrapper {
  position: relative;

  select {
    box-shadow: var(--sp-comp-dtv-select-box-shadow, none);
    position: relative;
    width: var(--sp-comp-dtv-select-width, 100%);
    border: var(--sp-comp-dtv-select-border, var(--sp-sys-form-field-border, 1px solid #ccc));
    appearance: none;
    -webkit-appearance: none;
    min-height: var(--sp-comp-dtv-select-min-height, var(--sp-sys-form-field-min-height));
    padding: var(--sp-comp-dtv-select-padding-block, var(--sp-sys-form-field-padding-block)) var(--padding-inline);
    padding-right: calc(var(--caret-size) + var(--caret-right-offset) + var(--sp-ref-scale-12));
    font: var(--sp-comp-dtv-select-font, var(--sp-sys-form-field-font-short));
    border-radius: var(--sp-comp-dtv-select-border-radius, var(--sp-sys-shape-control, 0));
    color: var(--color);
    cursor: pointer;
    background-color: transparent;
    text-overflow: ellipsis;

    &:focus-visible,
    &:focus {
      border: var(--sp-sys-form-field-focus-border);
      outline: var(--sp-sys-form-field-focus-outline);
      outline-offset: var(--sp-sys-form-field-focus-outline-offset);
    }
  }

  .caret {
    position: absolute;
    top: 50%;
    right: var(--caret-right-offset);
    transform: translateY(-75%) rotate(45deg);
    width: var(--caret-size);
    height: var(--caret-size);

    border-bottom: var(--sp-comp-dtv-select-caret-border, var(--sp-sys-form-field-border, 1px solid #ccc));
    border-right: var(--sp-comp-dtv-select-caret-border, var(--sp-sys-form-field-border, 1px solid #ccc));
  }
}
</style>
